import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TableHead from '@mui/material/TableHead';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery, Box, LinearProgress } from '@mui/material';

import TablePaginationActions from '../../components/TablePaginationActions';
import ModalDiffViewer from '../../components/ModalDiffViewer';

export default function LogsTable({
  titles,
  data,
  paginationMeta,
  loading = false,
  onPageChange,
  logsModel,
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width:820px)');

  // Update local pagination state when server data changes
  useEffect(() => {
    if (paginationMeta) {
      setPage(paginationMeta.page - 1); // Convert 1-based to 0-based for MUI
      setRowsPerPage(paginationMeta.limit);
    }
  }, [paginationMeta]);

  // Reset page when model filter changes (without triggering onPageChange)
  useEffect(() => {
    setPage(0);
    // Don't call onPageChange here to avoid infinite loops
    // The parent component already handles model changes
  }, [logsModel]);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
    if (onPageChange) {
      onPageChange(newPage, rowsPerPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    if (onPageChange) {
      onPageChange(0, newRowsPerPage);
    }
  };

  const renderMobileCard = (row, rowIndex) => {
    return (
      <Paper
        key={rowIndex}
        sx={{
          padding: '16px',
          marginBottom: '12px',
          borderRadius: '12px',
          border: '1px solid var(--table-border)',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: 'var(--table-hover)',
            transition: 'background-color 0.2s ease',
          },
        }}
      >
        {Object.keys(row)
          .filter((key) => key !== 'created_at' && key !== 'editor_description')
          .map((key, index) => {
            const visibleKeys = Object.keys(row).filter(
              (k) => k !== 'created_at' && k !== 'editor_description'
            );
            const titleIndex = Object.keys(row)
              .filter((k) => k !== 'created_at')
              .indexOf(key);
            const title = titles[titleIndex];

            const isLastItem = index === visibleKeys.length - 1;

            return (
              <div
                key={index}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: isLastItem ? '0px' : '8px',
                  fontSize: '0.875rem',
                  borderBottom: isLastItem ? 'none' : '1px solid #eee',
                  paddingBottom: isLastItem ? '0px' : '9px',
                  alignItems: 'flex-start',
                }}
              >
                <span
                  style={{
                    color: 'var(--text-secondary)',
                    fontWeight: 500,
                    minWidth: '180px',
                    width: '180px',
                    flexShrink: 0,
                  }}
                >
                  {title}:
                </span>
                <div
                  style={{
                    color: 'var(--text-primary)',
                    textAlign: 'left',
                    flex: 1,
                    wordBreak: 'break-word',
                  }}
                >
                  <span>{row[key]}</span>
                  {key === 'description' && row['editor_description'] && (
                    <div style={{ width: 'fit-content' }}>
                      <ModalDiffViewer content={row['editor_description']} />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
      </Paper>
    );
  };

  return (
    <div style={{ width: '100%', position: 'relative' }}>
      {/* Loading indicator overlay */}
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(2px)',
            borderRadius: '12px',
          }}
        >
          <LinearProgress
            sx={{
              borderRadius: '12px 12px 0 0',
              height: '3px',
              '& .MuiLinearProgress-bar': {
                transition: 'transform 0.2s ease',
              }
            }}
          />
        </Box>
      )}
      {/* Mobile View - Cards */}
      {isMobile ? (
        <div style={{ width: '100%', opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s ease' }}>
          {data && data.length > 0 ? (
            <>
              {data.map((row, rowIndex) => renderMobileCard(row, rowIndex))}

              {paginationMeta && paginationMeta.total > 0 && (
                <Paper
                  sx={{
                    borderRadius: '12px',
                    border: '1px solid var(--table-border)',
                    boxShadow: 'none',
                    marginTop: '16px',
                  }}
                >
                  <TablePagination
                    rowsPerPageOptions={[20, 30, 40, 50, 100]}
                    colSpan={3}
                    component="div"
                    sx={{
                      '& .MuiToolbar-root': {
                        paddingRight: '20px',
                        paddingLeft: '20px',
                        minHeight: '64px',
                      },
                      '& .MuiTablePagination-selectLabel': {
                        fontSize: '0.875rem',
                        color: 'var(--text-secondary)',
                      },
                      '& .MuiTablePagination-displayedRows': {
                        fontSize: '0.875rem',
                        color: 'var(--text-secondary)',
                        marginLeft: '10px',
                      },
                      '& .MuiTablePagination-input': {
                        marginRight: '8px',
                      },
                      '& .MuiTablePagination-actions': {
                        marginLeft: '10px',
                      },
                    }}
                    count={paginationMeta?.total || 0}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    align="right"
                    labelRowsPerPage={t('calendar.rowsPerPage')}
                    slotProps={{
                      select: {
                        inputProps: {
                          'aria-label': 'rows per page',
                        },
                        native: true,
                      },
                    }}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                  />
                </Paper>
              )}
            </>
          ) : (
            <Paper
              sx={{
                padding: '40px 20px',
                textAlign: 'center',
                color: 'var(--text-secondary)',
                fontStyle: 'italic',
                borderRadius: '12px',
                border: '1px solid var(--table-border)',
                boxShadow: 'none',
              }}
            >
              No data available
            </Paper>
          )}
        </div>
      ) : (
        /* Desktop View - Table */
        <div style={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s ease' }}>
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: 'none',
              borderRadius: '12px 12px 0 0',
              border: '1px solid var(--table-border)',
              borderBottom: 'none',
              overflowX: 'auto',
              overflowY: 'hidden',
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'var(--table-hover)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'var(--text-secondary)',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: 'var(--text-primary)',
                },
              },
            }}
          >
            <Table
              sx={{
                minWidth: 650,
                width: '100%',
                tableLayout: 'fixed',
                '& .MuiTableCell-root': {
                  borderBottom: 'none !important',
                },
              }}
              aria-label="audit logs table"
            >
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: 'var(--table-hover)',
                    '& .MuiTableCell-head': {
                      borderBottom: 'none',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: 'var(--table-header-text)',
                      padding: '16px 20px',
                    },
                  }}
                >
                  {titles.map((title, index) => (
                    <TableCell
                      key={index}
                      align={index === 0 ? 'left' : 'left'}
                      sx={{
                        width:
                          index === 0
                            ? '8%' // Number column
                            : index === 1
                            ? '18%' // Title column
                            : index === 2
                            ? '25%' // Description column
                            : index === 3
                            ? '20%' // Modified by column
                            : index === 4
                            ? '15%' // Model column
                            : '14%', // Created at column
                      }}
                    >
                      {title}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              {data && (
                <TableBody>
                  {data.map((row, rowIndex) => {
                    const isLastRow = rowIndex === data.length - 1;

                    return (
                      <TableRow
                        key={rowIndex}
                        sx={{
                          borderBottom: isLastRow ? 'none' : '1px solid #eee',
                          '&:hover': {
                            backgroundColor: 'var(--table-hover)',
                            transition: 'background-color 0.2s ease',
                          },
                          '& .MuiTableCell-body': {
                            borderBottom: 'none',
                            color: 'var(--text-primary)',
                            fontSize: '0.875rem',
                            padding: '16px 20px',
                          },
                        }}
                      >
                        {Object.keys(row).map(
                          (key, cellIndex) =>
                            key !== 'created_at' && (
                              <TableCell
                                key={cellIndex}
                                align="left"
                                sx={{
                                  overflowWrap: 'break-word',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {key !== 'editor_description' && row[key]}
                                {key === 'description' && row['editor_description'] && (
                                  <ModalDiffViewer content={row['editor_description']} />
                                )}
                              </TableCell>
                            )
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              )}
            </Table>
          </TableContainer>
          {paginationMeta && paginationMeta.total > 0 && (
            <Paper
              sx={{
                borderRadius: '0 0 12px 12px',
                border: '1px solid var(--table-border)',
                boxShadow: 'none',
              }}
            >
              <TablePagination
                rowsPerPageOptions={[20, 30, 40, 50, 100]}
                colSpan={3}
                component="div"
                sx={{
                  '& .MuiToolbar-root': {
                    paddingRight: '20px',
                    paddingLeft: '20px',
                    minHeight: '64px',
                  },
                  '& .MuiTablePagination-selectLabel': {
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                  },
                  '& .MuiTablePagination-displayedRows': {
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                    '@media (max-width: 820px)': {
                      marginLeft: '10px',
                    },
                  },
                  '& .MuiTablePagination-input': {
                    '@media (max-width: 820px)': {
                      marginRight: '8px',
                    },
                  },
                  '& .MuiTablePagination-actions': {
                    '@media (max-width: 820px)': {
                      marginLeft: '10px',
                    },
                  },
                }}
                count={paginationMeta?.total || 0}
                rowsPerPage={rowsPerPage}
                page={page}
                align="right"
                labelRowsPerPage={t('calendar.rowsPerPage')}
                slotProps={{
                  select: {
                    inputProps: {
                      'aria-label': 'rows per page',
                    },
                    native: true,
                  },
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
              />
            </Paper>
          )}
        </div>
      )}
    </div>
  );
}
